<!-- Paste/drag are bound at the FORM level, not on the dropzone: people paste wherever
     their caret happens to be, and drag anywhere over the form. -->
<form [formGroup]="form" (ngSubmit)="onSubmit()"
  (paste)="onPaste($event)"
  (dragover)="onDragOver($event)" (dragleave)="onDragLeave()" (drop)="onDrop($event)"
  class="relative space-y-7">

  @if (isDragging()) {
    <div class="absolute inset-0 z-20 rounded-2xl border-2 border-dashed border-primary bg-primary/10 backdrop-blur-[1px] flex items-center justify-center pointer-events-none">
      <span class="text-sm font-semibold text-primary">Drop your screenshot anywhere</span>
    </div>
  }

  <!-- ══════════ 1. What kind of issue — the page's one strong focal point ══════════
       Split by intent, not alphabetically. "Something's broken" and "I have an idea" are
       different journeys: the reproduce section below only exists for the former. -->
  <section>
    <h2 class="text-lg font-bold text-foreground mb-1">What would you like to tell us?</h2>
    <p class="text-sm text-muted-foreground mb-4">This decides what we ask you next.</p>

    <div class="space-y-5">
      <div>
        <p class="flex items-center gap-1.5 text-xs font-bold text-destructive uppercase tracking-wider mb-2.5">
          <span class="h-1.5 w-1.5 rounded-full bg-destructive"></span>
          Something's broken
        </p>
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
          @for (opt of brokenTypes; track opt.value) {
            <button type="button" (click)="selectType(opt.value)" [attr.aria-pressed]="isType(opt.value)"
              class="flex items-center gap-2 px-3.5 py-3 rounded-xl border text-sm font-medium text-left transition-all"
              [class]="isType(opt.value)
                ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                : 'border-border bg-card text-foreground hover:border-primary/50 hover:bg-primary/5'">
              <app-icon [name]="opt.icon" size="sm"
                [class]="isType(opt.value) ? 'text-primary-foreground shrink-0' : 'text-muted-foreground shrink-0'" />
              {{ opt.label }}
            </button>
          }
        </div>
      </div>

      <div>
        <p class="flex items-center gap-1.5 text-xs font-bold text-primary uppercase tracking-wider mb-2.5">
          <span class="h-1.5 w-1.5 rounded-full bg-primary"></span>
          I have an idea
        </p>
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          @for (opt of ideaTypes; track opt.value) {
            <button type="button" (click)="selectType(opt.value)" [attr.aria-pressed]="isType(opt.value)"
              class="flex items-center gap-2 px-3.5 py-3 rounded-xl border text-sm font-medium text-left transition-all"
              [class]="isType(opt.value)
                ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                : 'border-border bg-card text-foreground hover:border-primary/50 hover:bg-primary/5'">
              <app-icon [name]="opt.icon" size="sm"
                [class]="isType(opt.value) ? 'text-primary-foreground shrink-0' : 'text-muted-foreground shrink-0'" />
              {{ opt.label }}
            </button>
          }
        </div>
      </div>
    </div>
  </section>

  <!-- ══════════ 2. Describe it ══════════ -->
  <section class="space-y-4 pt-6 border-t border-border">
    <div>
      <div class="flex items-baseline justify-between mb-1.5">
        <label for="issue-title" class="text-sm font-semibold text-foreground">
          Title <span class="text-destructive">*</span>
        </label>
        @if (showTitleCount()) {
          <span class="text-[11px] tabular-nums text-muted-foreground">{{ titleLength() }}/140</span>
        }
      </div>
      <input id="issue-title" type="text" formControlName="title" maxlength="140"
        [placeholder]="isIdea() ? 'Summarise your idea in one line' : 'Briefly describe what went wrong'"
        class="w-full px-3.5 py-2.5 rounded-xl bg-card border border-border shadow-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus-visible:outline-none transition-colors" />
      @if (form.controls.title.touched && form.controls.title.invalid) {
        <p class="mt-1 text-xs text-destructive">A title is required (max 140 characters).</p>
      }
    </div>

    <div>
      <div class="flex items-baseline justify-between mb-1.5">
        <label for="issue-description" class="text-sm font-semibold text-foreground">
          Description <span class="text-destructive">*</span>
        </label>
        @if (showDescriptionCount()) {
          <span class="text-[11px] tabular-nums text-muted-foreground">{{ descriptionLength() }}/5000</span>
        }
      </div>
      <textarea id="issue-description" formControlName="description" rows="4" maxlength="5000"
        [placeholder]="isIdea() ? 'What would you like HashBooks to do, and why?' : 'What happened?'"
        class="w-full px-3.5 py-2.5 rounded-xl bg-card border border-border shadow-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus-visible:outline-none transition-colors resize-y"></textarea>
      @if (form.controls.description.touched && form.controls.description.invalid) {
        <p class="mt-1 text-xs text-destructive">A description is required.</p>
      }
    </div>

    <div>
      <label for="module" class="block text-sm font-semibold text-foreground mb-1.5">
        Where did this happen? <span class="text-muted-foreground font-normal">(optional)</span>
      </label>
      <app-select id="module" class="block w-full"
        formControlName="module" [options]="moduleOptions" placeholder="Not specified" />
    </div>
  </section>

  <!-- ══════════ Conditional context (§1.7) ══════════ -->

  @if (showTaxFields()) {
    <div class="grid gap-4 sm:grid-cols-2 p-4 rounded-xl bg-primary/[0.04] border border-primary/20 animate-fade-in">
      <div class="sm:col-span-2 flex items-center gap-2">
        <app-icon name="calculator" size="xs" class="text-primary" />
        <span class="text-xs font-bold text-primary uppercase tracking-wide">Tax details</span>
      </div>
      <div>
        <label for="tax-year" class="block text-sm font-medium text-foreground mb-1.5">
          Tax Year <span class="text-destructive">*</span>
        </label>
        <app-select id="tax-year" class="block w-full" formControlName="tax_year"
          [options]="taxYearOptions()"
          [invalid]="form.controls.tax_year.touched && form.controls.tax_year.invalid"
          placeholder="Select a year" />
      </div>
      <div>
        <label for="jurisdiction" class="block text-sm font-medium text-foreground mb-1.5">
          Jurisdiction <span class="text-destructive">*</span>
        </label>
        <input id="jurisdiction" type="text" formControlName="jurisdiction"
          class="w-full px-3.5 py-2.5 rounded-xl bg-card border border-border shadow-sm text-foreground focus:border-primary focus-visible:outline-none transition-colors" />
        <p class="mt-1 text-xs text-muted-foreground">Defaults to your tax jurisdiction.</p>
      </div>
      <div class="sm:col-span-2">
        <label for="report-id" class="block text-sm font-medium text-foreground mb-1.5">
          Report ID <span class="text-muted-foreground font-normal">(optional)</span>
        </label>
        <input id="report-id" type="text" formControlName="report_id"
          placeholder="If this came from a generated report"
          class="w-full px-3.5 py-2.5 rounded-xl bg-card border border-border shadow-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus-visible:outline-none transition-colors" />
      </div>
    </div>
  }

  @if (showSyncFields()) {
    <div class="p-4 rounded-xl bg-primary/[0.04] border border-primary/20 space-y-3 animate-fade-in">
      <div class="flex items-center gap-2">
        <app-icon name="refresh-cw" size="xs" class="text-primary" />
        <span class="text-xs font-bold text-primary uppercase tracking-wide">Sync details</span>
      </div>
      <div>
        <label for="connection" class="block text-sm font-medium text-foreground mb-1.5">
          Wallet / Exchange <span class="text-destructive">*</span>
        </label>
        <!-- Populated ONLY from the user's own connections. -->
        <app-select id="connection" class="block w-full" formControlName="connection_id"
          [options]="connectionOptions()"
          [invalid]="form.controls.connection_id.touched && form.controls.connection_id.invalid"
          placeholder="Select a connection" />
        @if (!connections().length) {
          <p class="mt-1 text-xs text-muted-foreground">You have no connected wallets or exchanges yet.</p>
        }
      </div>

      <!-- Auto-attached, read-only: support shouldn't have to ask for these. -->
      @if (selectedConnection(); as conn) {
        <div class="grid gap-2 sm:grid-cols-2 text-xs">
          <div class="px-3 py-2 rounded-lg bg-card border border-border">
            <span class="text-muted-foreground">Last sync:</span>
            <span class="ml-1 text-foreground font-medium">
              {{ conn.last_synced_at ? (conn.last_synced_at | date: 'medium') : 'Never' }}
            </span>
          </div>
          <div class="px-3 py-2 rounded-lg bg-card border border-border">
            <span class="text-muted-foreground">Chain / Network:</span>
            <span class="ml-1 text-foreground font-medium">{{ conn.chain?.name ?? '—' }}</span>
          </div>
        </div>
      }
    </div>
  }

  @if (showPricingFields()) {
    <div class="grid gap-4 sm:grid-cols-2 p-4 rounded-xl bg-primary/[0.04] border border-primary/20 animate-fade-in">
      <div class="sm:col-span-2 flex items-center gap-2">
        <app-icon name="dollar-sign" size="xs" class="text-primary" />
        <span class="text-xs font-bold text-primary uppercase tracking-wide">Pricing details</span>
      </div>
      <div>
        <label for="asset" class="block text-sm font-medium text-foreground mb-1.5">
          Asset <span class="text-destructive">*</span>
        </label>
        <input id="asset" type="text" formControlName="asset" placeholder="e.g. ETH"
          class="w-full px-3.5 py-2.5 rounded-xl bg-card border border-border shadow-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus-visible:outline-none transition-colors" />
      </div>
      <div>
        <label for="price-date" class="block text-sm font-medium text-foreground mb-1.5">
          Date <span class="text-destructive">*</span>
        </label>
        <input id="price-date" type="date" formControlName="date"
          class="w-full px-3.5 py-2.5 rounded-xl bg-card border border-border shadow-sm text-foreground focus:border-primary focus-visible:outline-none transition-colors" />
      </div>
      <div class="sm:col-span-2">
        <label for="price-source" class="block text-sm font-medium text-foreground mb-1.5">
          Expected price / source <span class="text-muted-foreground font-normal">(optional)</span>
        </label>
        <input id="price-source" type="text" formControlName="expected_price_source"
          placeholder="e.g. CoinGecko showed $3,120"
          class="w-full px-3.5 py-2.5 rounded-xl bg-card border border-border shadow-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus-visible:outline-none transition-colors" />
      </div>
    </div>
  }

  @if (showPerformanceFields()) {
    <div class="p-4 rounded-xl bg-primary/[0.04] border border-primary/20 animate-fade-in">
      <div class="flex items-center gap-2 mb-3">
        <app-icon name="zap" size="xs" class="text-primary" />
        <span class="text-xs font-bold text-primary uppercase tracking-wide">Performance details</span>
      </div>
      <label for="occurred-at" class="block text-sm font-medium text-foreground mb-1.5">
        Approximately when did this happen?
      </label>
      <input id="occurred-at" type="datetime-local" formControlName="occurred_at"
        class="w-full px-3.5 py-2.5 rounded-xl bg-card border border-border shadow-sm text-foreground focus:border-primary focus-visible:outline-none transition-colors" />
    </div>
  }

  <!-- ══════════ 3. Reproduce — defects only ══════════
       An idea has no steps and no expected-vs-actual. Rendering this block for a feature
       request asks the user to leave three boxes blank, which is worse than not asking.

       No Severity field, deliberately. Severity was user-supplied and barred from driving
       routing, sorting, dashboards or alerting — so asking for it collected a value that
       changed nothing, while implying to the user that it set their queue position.
       Priority is set by an admin and is the only thing that triages. -->
  @if (!isIdea()) {
    <section class="space-y-4 pt-6 border-t border-border animate-fade-in">
      <div>
        <h2 class="text-sm font-bold text-foreground">Help us reproduce it</h2>
        <p class="text-xs text-muted-foreground">Optional — but this is what lets an engineer fix it without asking you again.</p>
      </div>

      <!-- Steps gets the full width: it needs 2–3× the room of the other two, and an
           equal-thirds grid left it wrapping in a mostly-empty box. -->
      <div>
        <label for="steps" class="block text-sm font-semibold text-foreground mb-1.5">Steps to Reproduce</label>
        <textarea id="steps" formControlName="steps_to_reproduce" rows="4"
          (focus)="onStepsFocus()" (blur)="onStepsBlur()" (keydown)="onStepsKeydown($event)"
          placeholder="1. Go to Transactions&#10;2. Filter by 2024–25&#10;3. …"
          class="w-full px-3.5 py-2.5 rounded-xl bg-card border border-border shadow-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus-visible:outline-none transition-colors resize-y"></textarea>
        <p class="mt-1 text-xs text-muted-foreground">Press Enter for the next step — we'll number it for you.</p>
      </div>

      <div class="grid gap-4 sm:grid-cols-2">
        <div>
          <label for="expected" class="block text-sm font-semibold text-foreground mb-1.5">Expected</label>
          <textarea id="expected" formControlName="expected_behavior" rows="3"
            placeholder="What should have happened?"
            class="w-full px-3.5 py-2.5 rounded-xl bg-card border border-border shadow-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus-visible:outline-none transition-colors resize-y"></textarea>
        </div>
        <div>
          <label for="actual" class="block text-sm font-semibold text-foreground mb-1.5">Actual</label>
          <textarea id="actual" formControlName="actual_behavior" rows="3"
            placeholder="What happened instead?"
            class="w-full px-3.5 py-2.5 rounded-xl bg-card border border-border shadow-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus-visible:outline-none transition-colors resize-y"></textarea>
        </div>
      </div>
    </section>
  }

  <!-- ══════════ 4. Screenshots ══════════ -->
  <section class="space-y-3 pt-6 border-t border-border">
    <div class="flex items-baseline justify-between">
      <div>
        <h2 class="text-sm font-bold text-foreground">Screenshots</h2>
        <p class="text-xs text-muted-foreground">A picture of the broken screen beats a paragraph describing it.</p>
      </div>
      <span class="shrink-0 text-[11px] tabular-nums text-muted-foreground">
        {{ attachments().length }}/{{ maxAttachments }}
      </span>
    </div>

    @if (attachments().length < maxAttachments) {
      <label
        class="flex flex-col items-center justify-center gap-2 px-4 py-8 rounded-xl border-2 border-dashed border-border bg-muted/20 hover:border-primary/50 hover:bg-primary/[0.04] cursor-pointer transition-colors">
        <span class="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center mb-0.5">
          <app-icon name="upload" size="md" class="text-primary" />
        </span>
        <span class="text-sm font-semibold text-foreground">Drop an image, or just paste it</span>
        <span class="text-xs text-muted-foreground">
          Ctrl&nbsp;+&nbsp;V works anywhere on this form · PNG, JPEG or WebP, up to 5&nbsp;MB
        </span>
        <input type="file" [accept]="acceptAttr" multiple class="hidden" (change)="onFilesSelected($event)" />
      </label>
    }

    @if (attachments().length) {
      <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        @for (a of attachments(); track a.previewUrl) {
          <div class="group relative overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-shadow hover:shadow-md">
            <img [src]="a.previewUrl" [alt]="a.file.name" class="h-28 w-full object-cover" />

            @if (a.uploading) {
              <div class="absolute inset-0 flex items-center justify-center bg-background/70 backdrop-blur-sm">
                <span class="text-xs font-medium text-muted-foreground">Uploading…</span>
              </div>
            }
            @if (a.error) {
              <div class="absolute inset-0 flex items-center justify-center bg-destructive/15 p-2">
                <span class="text-xs text-destructive text-center">{{ a.error }}</span>
              </div>
            }

            <button type="button" (click)="removeAttachment(a)"
              [attr.aria-label]="'Remove ' + a.file.name"
              class="absolute top-1.5 right-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-card/95 border border-border opacity-0 shadow-sm transition-opacity group-hover:opacity-100 focus:opacity-100">
              <app-icon name="x" size="sm" class="text-destructive" />
            </button>

            <div class="truncate border-t border-border bg-muted/30 px-2 py-1.5 text-xs text-muted-foreground">{{ a.file.name }}</div>
          </div>
        }
      </div>
    }
  </section>

  <!-- ══════════ 5. What we'll attach (§1.6) ══════════
       Expanded by default. This is the most trust-building content on the page and it was
       collapsed at the bottom, below the fold — i.e. invisible to everyone it was written
       for. Lead with the reassurance, then show the actual values. -->
  <section class="pt-6 border-t border-border">
    <div class="rounded-xl border border-border bg-card overflow-hidden">
      <button type="button" (click)="toggleDetails()" [attr.aria-expanded]="detailsExpanded()"
        class="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-muted/40 transition-colors text-left">
        <span class="shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <app-icon name="shield" size="sm" class="text-primary" />
        </span>
        <span class="flex-1 min-w-0">
          <span class="block text-sm font-semibold text-foreground">Here's exactly what we'll attach</span>
          <span class="block text-xs text-muted-foreground">
            Your browser and account details. Never your wallet addresses — unless you tick the box below.
          </span>
        </span>
        <app-icon [name]="detailsExpanded() ? 'chevron-up' : 'chevron-down'" size="sm"
          class="text-muted-foreground shrink-0" />
      </button>

      @if (detailsExpanded()) {
        <!-- Lists the ACTUAL values, not a generic description. A user cannot consent to
             "we may collect wallet details" — they can only consent to seeing 0x1234…
             leave their machine. -->
        <dl class="px-4 py-3 space-y-1.5 text-xs border-t border-border">
          <div class="flex gap-2"><dt class="text-muted-foreground w-40 shrink-0">Name</dt><dd class="text-foreground break-all">{{ accountName() }}</dd></div>
          <div class="flex gap-2"><dt class="text-muted-foreground w-40 shrink-0">Account email</dt><dd class="text-foreground break-all">{{ accountEmail() }}</dd></div>
          <div class="flex gap-2"><dt class="text-muted-foreground w-40 shrink-0">Tax jurisdiction</dt><dd class="text-foreground">{{ jurisdiction() }}</dd></div>
          <div class="flex gap-2"><dt class="text-muted-foreground w-40 shrink-0">Browser</dt><dd class="text-foreground break-all">{{ env.browser }} {{ env.browser_version }}</dd></div>
          <div class="flex gap-2"><dt class="text-muted-foreground w-40 shrink-0">Operating system</dt><dd class="text-foreground">{{ env.operating_system }}</dd></div>
          <div class="flex gap-2"><dt class="text-muted-foreground w-40 shrink-0">Device</dt><dd class="text-foreground">{{ env.device }}</dd></div>
          <div class="flex gap-2"><dt class="text-muted-foreground w-40 shrink-0">Screen</dt><dd class="text-foreground">{{ env.screen_resolution }}</dd></div>
          <div class="flex gap-2"><dt class="text-muted-foreground w-40 shrink-0">Timezone</dt><dd class="text-foreground">{{ env.timezone }}</dd></div>
          <div class="flex gap-2"><dt class="text-muted-foreground w-40 shrink-0">Language</dt><dd class="text-foreground">{{ env.language }}</dd></div>
          <div class="flex gap-2"><dt class="text-muted-foreground w-40 shrink-0">App version</dt><dd class="text-foreground">{{ env.app_version }}</dd></div>
          <div class="flex gap-2"><dt class="text-muted-foreground w-40 shrink-0">Current page</dt><dd class="text-foreground break-all">{{ env.current_url }}</dd></div>

          @if (hasTier2Data()) {
            <div class="pt-2 mt-2 border-t border-border">
              <p class="text-muted-foreground mb-1.5">Only included if you tick the box below:</p>
              @if (walletAddresses().length) {
                <div class="flex gap-2">
                  <dt class="text-muted-foreground w-40 shrink-0">Wallet address(es)</dt>
                  <dd class="text-foreground break-all">
                    @for (w of walletAddresses(); track w) { <div>{{ w }}</div> }
                  </dd>
                </div>
              }
              @if (exchangeNames().length) {
                <div class="flex gap-2 mt-1.5">
                  <dt class="text-muted-foreground w-40 shrink-0">Connected exchange(s)</dt>
                  <dd class="text-foreground break-all">{{ exchangeNames().join(', ') }}</dd>
                </div>
              }
            </div>
          }
        </dl>
      }
    </div>

    @if (hasTier2Data()) {
      <!-- Opt-in, unchecked by default. Unticked means the values are never sent AND the
           server does not backfill them from the account. The copy leads with the BENEFIT:
           "you can submit without them" is true but gives the user no reason to say yes. -->
      <label class="mt-3 flex items-start gap-2.5 cursor-pointer rounded-xl border border-border bg-card hover:border-primary/40 p-3.5 transition-colors">
        <input type="checkbox" formControlName="technical_details_consent"
          class="mt-0.5 w-4 h-4 rounded border-border text-primary accent-primary focus:ring-2 focus:ring-primary/40" />
        <span class="text-sm text-foreground">
          Include my wallet and exchange details.
          <span class="block text-xs text-muted-foreground mt-0.5">
            Sync and tax bugs are usually impossible to reproduce without these. Optional — you can submit without them.
          </span>
        </span>
      </label>
    }
  </section>

  <!-- ══════════ Sticky submit bar ══════════
       The form is several screens tall, so a submit button at the bottom is off-screen for
       almost the whole time you're filling it in. The quality meter lives here too: it's
       the one place the user is always looking at. -->
  <!-- No negative margins to bleed to the card edge: this same form renders inside the
       modal (px-6 py-5) as well as the page card (p-6 md:p-8), and a hardcoded bleed
       would overshoot in one of them. A self-contained floating bar is correct in both. -->
  <div class="sticky bottom-2 z-10 px-4 py-3 bg-card/95 backdrop-blur border border-border rounded-xl shadow-lg">
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div class="flex-1 min-w-[200px]">
        <div class="flex items-center gap-2 mb-1.5">
          <span class="text-xs font-semibold text-foreground">{{ qualityLabel() }}</span>
          <span class="text-[11px] tabular-nums text-muted-foreground">{{ qualityScore() }}%</span>
        </div>
        <div class="h-1.5 w-full max-w-[260px] rounded-full bg-muted overflow-hidden">
          <div class="h-full rounded-full bg-primary transition-all duration-300"
            [style.width.%]="qualityScore()"></div>
        </div>
        <div class="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
          @for (c of qualityChecks(); track c.label) {
            @if (!c.done) {
              <span class="text-[11px] text-muted-foreground">+ {{ c.label }}</span>
            }
          }
        </div>
      </div>

      <div class="flex items-center gap-3">
        <app-button type="button" variant="ghost" (click)="onCancel()">Cancel</app-button>
        <app-button type="submit" variant="primary" [disabled]="submitting() || anyUploading()">
          {{ submitting() ? 'Submitting…' : 'Submit Report' }}
        </app-button>
      </div>
    </div>
  </div>
</form>
